import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    InputNumber
} from 'antd';
import * as AntIcons from '@ant-design/icons';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
const { TextArea } = Input;
const { Option } = Select;


interface Module {
    module_id: number;
    module_name: string;
    module_url: string;
    module_parent: number;
    module_type: string;
    module_status: number;
    module_order: number;
    module_icon: string;
}

interface ModuleModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        module_id?: number;
        module_name?: string;
        module_url: string;
        module_parent: number;
        module_type: string;
        module_icon: string;
        module_order: number;
    } | null;
    isEditMode?: boolean;
    modules: Module[];
}
const iconOptions = Object.keys(AntIcons)
    .filter((name) => name.endsWith('Outlined')) // optional: only use outlined icons
    .map((name) => {
        const IconComponent = (AntIcons as any)[name];
        return {
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconComponent />
                    {name}
                </span>
            ),
            value: name,
        };
    });

const ModuleModel: React.FC<ModuleModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    modules = [],
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddModule = async (values: any) => {
        try {
            const dataVal = {
                module_name: values.module_name,
                module_url: values.module_url,
                module_parent: values.module_parent,
                module_type: values.module_type,
                module_order: values.module_order,
                module_icon: values.module_icon,
                ...(initialValues?.module_id && { module_id: initialValues.module_id }),
            };
            const url = isEditMode
                ? 'edit-module'
                : 'add-module';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Module updated successfully!' : 'Module created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} module`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                module_name: initialValues.module_name,
                module_url: initialValues.module_url || undefined,
                module_icon: initialValues.module_icon || undefined,
                module_order: initialValues.module_order || undefined,
                module_type: initialValues.module_type || undefined,
                module_parent: initialValues.module_parent ?? 0,
            });
        } else if (visible) {
            form.resetFields();
        }
        console.log("initialValues", initialValues)
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit Module' : 'Add New Module'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddModule}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="module_parent"
                            className="px-8"
                            label="Module Parent"
                            rules={[
                                { required: true, message: 'Please select a parent module' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (isEditMode && initialValues?.module_id && value === initialValues.module_id) {
                                            return Promise.reject(new Error("Module cannot be the parent of itself."));
                                        }
                                        return Promise.resolve();
                                    }
                                })
                            ]}
                        >
                            <Select
                                placeholder="Select Parent Module"
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    { value: 0, label: 'Root' },
                                    ...modules.map((module) => ({
                                        value: module.module_id,
                                        label: module.module_name || module.module_url,
                                    })),
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="module_name"
                            className='px-8'
                            label={
                                <span>
                                    Module Name&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 100 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Module Name' },
                                { max: 100, message: 'Maximum 100 characters allowed' },
                                {
                                    validator(_, value) {
                                        if (value && value.trim().length === 0) {
                                            return Promise.reject("Module Name cannot be empty or spaces only");
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={100}
                                placeholder="e.g., User Master, Dashboard"

                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="module_type"
                            className="px-8"
                            label={
                                <span>
                                    Module Type&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Select one option)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please select the Module Type' },
                            ]}
                        >
                            <Select placeholder="Select Module Type">
                                <Option value="page">Page</Option>
                                <Option value="function">Function</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="module_url"
                            className="px-8"
                            label={
                                <span>
                                    Module URL&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 100 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Module URL' },
                                { max: 100, message: 'Maximum 100 characters allowed' },

                            ]}
                        >
                            <Input
                                showCount
                                maxLength={100}
                                placeholder="e.g., /dashboard, /module-name"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="module_order"
                            className="px-8"
                            label={
                                <span>
                                    Module Order
                                </span>
                            }

                            rules={[
                                {
                                    validator(_, value) {
                                        if (!value) return Promise.resolve();
                                        if (!/^\d+$/.test(value)) {
                                            return Promise.reject("Order must contain digits only");
                                        }
                                        if (String(value).length > 6) {
                                            return Promise.reject("Order cannot exceed 6 digits");
                                        }
                                        if (value < 0) {
                                            return Promise.reject("Order cannot be negative");
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <InputNumber
                                className="py-7 border-radious"
                                maxLength={6}
                                minLength={0}
                                placeholder="Order By"
                                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                    const nativeEvent = e.nativeEvent as InputEvent;
                                    const inputChar = nativeEvent.data;
                                    const currentValue = (e.target as HTMLInputElement).value;

                                    if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 6) {
                                        e.preventDefault();
                                    }
                                }}
                                style={{ width: '100%' }} // Optional styling
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="module_icon"
                            className="px-8"
                            label={<span>Module Icon</span>}
                            rules={[{ max: 25, message: 'Maximum 25 characters allowed' }]}
                        >
                            <Select
                                placeholder="Select Module Icon"
                                showSearch
                                optionFilterProp="value"
                                filterOption={(input, option) =>
                                    String(option?.label?.props?.children[1] ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                options={iconOptions}
                            />
                        </Form.Item>

                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Modules
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ModuleModel;
